/**
 * Cuentas por Pagar API client.
 *
 * The payables module is paceBI-owned and makes NO Pacesetter write, so unlike
 * the AR/anticipo clients there is no writes_enabled consideration here — only
 * the ap:view / ap:capture / ap:pay permissions, enforced server-side.
 */
import type {
  ApAgingBucket, ApParseJob, ApVendorsResponse,
  PayableDetail, PayablesList,
} from './types';

export interface PayablesQuery {
  status?: string;
  rfc?: string;
  from?: string;
  to?: string;
  moneda?: string;
  sid?: number;
  onum?: number;
  limit?: number;
}

export interface PayablePatch {
  rfc?: string;
  emisor_nombre?: string;
  serie?: string;
  folio?: string;
  fecha?: string;
  due_date?: string;
  moneda?: string;
  tipo_cambio?: number;
  subtotal?: number;
  descuento?: number;
  impuestos?: number;
  total?: number;
  metodo_pago?: string;
  forma_pago?: string;
  tipo_comprobante?: string;
  notes?: string;
  /** Omit to leave existing lines untouched; send an array to replace them. */
  lines?: Array<{
    seq: number; cantidad: number; unidad: string; no_identificacion: string;
    descripcion: string; valor_unitario: number; descuento: number; importe: number;
  }>;
}

export interface VendorMapInput {
  rfc: string;
  emisor_nombre?: string;
  sup_line?: string;
  sup_id?: string;
  sup_name?: string;
  terms_days?: number;
}

export interface ApPaymentInput {
  paid_at: string;
  moneda: string;
  amount: number;
  method: string;
  reference: string;
  bank: string;
  notes: string;
  applications: Array<{ invoice_id: string; amount: number }>;
}

function buildQS(params: { [k: string]: unknown }): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? '?' + parts.join('&') : '';
}

/**
 * The cookie is `pacebi_csrf` (internal/auth/csrf.go), NOT `csrf_token` — same
 * name anticipos.ts / payments.ts / facturacion.ts read. Getting it wrong sends
 * an empty X-CSRF-Token and RequireCSRF 403s every write.
 */
function csrf(): string {
  const m = document.cookie.match(/(?:^|;\s*)pacebi_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) {
    // The Go handlers put the operator-facing reason in the body (e.g. the
    // currency-mismatch message), so surface that rather than a bare status.
    const detail = await res.text().catch(() => '');
    throw new Error(detail.trim() || `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function writeInit(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() },
    body: JSON.stringify(body),
  };
}

export function fetchPayables(q: PayablesQuery): Promise<PayablesList> {
  return req<PayablesList>('/api/payables' + buildQS({ ...q }));
}

export function fetchPayable(id: string): Promise<PayableDetail> {
  return req<PayableDetail>(`/api/payables/${encodeURIComponent(id)}`);
}

export async function fetchPayablesAging(): Promise<ApAgingBucket[]> {
  const r = await req<{ buckets: ApAgingBucket[] }>('/api/payables/aging');
  return r.buckets ?? [];
}

export function fetchPayablesVendors(): Promise<ApVendorsResponse> {
  return req<ApVendorsResponse>('/api/payables/vendors');
}

/**
 * uploadPayable posts the files as multipart. Deliberately no Content-Type
 * header: the browser must set it so the boundary is included.
 */
export function uploadPayable(
  files: { xml?: File; pdf?: File },
  ctx?: { sid?: number; onum?: number },
): Promise<{ invoice_id: string; job_id: string }> {
  const fd = new FormData();
  if (files.xml) fd.append('xml', files.xml);
  if (files.pdf) fd.append('pdf', files.pdf);
  if (ctx?.sid) fd.append('sid', String(ctx.sid));
  if (ctx?.onum) fd.append('onum', String(ctx.onum));
  return req<{ invoice_id: string; job_id: string }>('/api/payables/upload', {
    method: 'POST',
    headers: { 'X-CSRF-Token': csrf() },
    body: fd,
  });
}

export function fetchParseJob(jobId: string): Promise<ApParseJob> {
  return req<ApParseJob>(`/api/payables/jobs/${encodeURIComponent(jobId)}`);
}

export function savePayable(id: string, patch: PayablePatch): Promise<PayableDetail> {
  return req<PayableDetail>(`/api/payables/${encodeURIComponent(id)}`, writeInit('PATCH', patch));
}

export async function cancelPayable(id: string): Promise<void> {
  await req<{ ok: boolean }>(`/api/payables/${encodeURIComponent(id)}`,
    writeInit('PATCH', { cancel: true }));
}

export function mapVendor(v: VendorMapInput): Promise<{ backfilled: number }> {
  return req<{ backfilled: number }>('/api/payables/vendors', writeInit('POST', v));
}

export function recordApPayment(p: ApPaymentInput): Promise<{ payment_id: string }> {
  return req<{ payment_id: string }>('/api/payables/payments', writeInit('POST', p));
}

export async function voidApPayment(id: string, reason: string): Promise<void> {
  await req<{ ok: boolean }>(`/api/payables/payments/${encodeURIComponent(id)}/void`,
    writeInit('POST', { reason }));
}

/**
 * The literal segment comes second (/file/{id}, not /{id}/file) because Go's
 * mux cannot disambiguate "/api/payables/{id}/file" from
 * "/api/payables/jobs/{id}" — see handlers_payables.go.
 */
export function payableFileUrl(id: string, kind: 'xml' | 'pdf'): string {
  return `/api/payables/file/${encodeURIComponent(id)}?kind=${kind}`;
}
